// Execute `fn` at a specific interval, ensuring that the current invocation of `fn`
// completes before the next one starts. This serves as an alternative to `setInterval`,
// but providing protection against overlapping invocations of `fn`. It also includes
// debounce logic so that redundant start/restart calls within the same tick are collapsed.

import EventEmitter from '../../controllers/eventEmitter/eventEmitter'

type RecurringTimeoutStartOptions = {
  timeout?: number
  /**
   * Whether to run the function immediately upon starting,
   * instead of waiting for the first timeout interval.
   */
  runImmediately?: boolean
  /**
   * Whether to allow the starting of a new function execution
   * even if the previous one is still running. There will still
   * be only one interval scheduled at a time.
   *
   * @example
   * - Execution 1 starts
   * - Execution 2 starts while Execution 1 is still running
   * - Execution 1 completes - a new execution is not scheduled. Simply
   * the promise of Execution 1 is resolved.
   * - Execution 2 completes - a new execution is scheduled.
   */
  allowOverlap?: boolean
}

export interface IRecurringTimeout {
  start: (options?: RecurringTimeoutStartOptions) => void
  restart: (options?: RecurringTimeoutStartOptions) => void
  stop: () => void
  updateTimeout: (options: { timeout: number }) => void
  running: boolean
  sessionId: number
  fnExecutionsCount: number
  startedRunningAt: number
  currentTimeout: number
  promise: Promise<void> | undefined
  startScheduled: boolean
}

export class RecurringTimeout implements IRecurringTimeout {
  #id: string | undefined // for debugging

  #timeoutId?: NodeJS.Timeout

  #emitError?: EventEmitter['emitError']

  #fn: () => Promise<void>

  // used mainly for testing how many times the fn was called
  sessionId: number = 0

  fnExecutionsCount: number = 0

  running = false

  startedRunningAt: number = 0

  currentTimeout: number

  promise: Promise<void> | undefined

  // collapse multiple start/restart calls in the same tick
  #pendingStart: RecurringTimeoutStartOptions | undefined

  // we use this id to prevent race conditions where a background-queued 12s wait
  // would block a foreground-queued immediate execution in the same tick.
  #internalSessionId = 0

  startScheduled = false

  constructor(
    fn: () => Promise<void>,
    timeout: number,
    emitError?: EventEmitter['emitError'],
    id?: string
  ) {
    this.#fn = fn
    this.currentTimeout = timeout
    this.#emitError = emitError
    this.#id = id
  }

  updateTimeout({ timeout }: { timeout: number }) {
    this.currentTimeout = timeout
  }

  start(opts: RecurringTimeoutStartOptions = {}) {
    this.#scheduleStart(opts)
  }

  stop() {
    this.#internalSessionId += 1
    this.sessionId = this.#internalSessionId
    this.startScheduled = false
    this.#reset()
  }

  restart(opts: RecurringTimeoutStartOptions = {}) {
    this.#internalSessionId += 1
    this.sessionId = this.#internalSessionId
    this.#reset()
    this.#scheduleStart(opts)
  }

  async #loop() {
    this.fnExecutionsCount += 1
    const currentCount = this.fnExecutionsCount

    try {
      this.promise = this.#fn()
      await this.promise
    } catch (err: any) {
      if (!this.promise) return
      console.error('Recurring task error:', err)
      if (this.#emitError) {
        // set level to silent as we don't want the user to see 'Recurring task failed'
        this.#emitError({ error: err, message: 'Recurring task failed', level: 'silent' })
      }
    } finally {
      // If fnExecutionsCount has changed, it means `restart` was called during the execution of fn,
      // so we shouldn't schedule the next loop here.
      if (this.promise && this.fnExecutionsCount === currentCount) {
        if (this.running) this.#timeoutId = setTimeout(this.#loop.bind(this), this.currentTimeout)
        this.promise = undefined
      }
    }
  }

  #scheduleStart(opts: RecurringTimeoutStartOptions = {}) {
    if (this.running) return
    this.#pendingStart = {
      timeout: opts.timeout ?? this.#pendingStart?.timeout,
      runImmediately: opts.runImmediately || this.#pendingStart?.runImmediately,
      allowOverlap: opts.allowOverlap || this.#pendingStart?.allowOverlap
    }
    if (this.startScheduled) return
    this.startScheduled = true

    const capturedSessionId = this.#internalSessionId

    queueMicrotask(() => {
      // If the session ID changed since we queued this microtask (e.g. stop() or restart() was called),
      // we self-destruct. This prevents a background-scheduled "wait" from blocking an immediate request.
      if (this.#internalSessionId !== capturedSessionId) return

      this.startScheduled = false
      const { timeout: newTimeout, runImmediately, allowOverlap } = this.#pendingStart || {}
      this.#pendingStart = undefined

      this.running = true
      this.startedRunningAt = Date.now()

      if (newTimeout) this.updateTimeout({ timeout: newTimeout })

      // Prevents starting a new loop if the previous one is still running
      if (this.promise && !allowOverlap) return

      if (runImmediately) {
        this.#loop()
      } else {
        this.#timeoutId = setTimeout(this.#loop.bind(this), this.currentTimeout)
      }
    })
  }

  #reset() {
    this.running = false
    this.startScheduled = false
    this.promise = undefined
    if (this.#timeoutId) {
      clearTimeout(this.#timeoutId)
      this.#timeoutId = undefined
    }
  }
}
